import React, { useContext } from 'react';
import { TextLayerExtension } from '@meitu/saas-editor-core/es/editor/layer-extensions/text-layer';
import { TextWritingType, type ITextLayerProps } from '@meitu/saas-editor-core/es/version-types';
import { FontStyleGroup, type Features } from '@/components/font-style-group';
import { useEditor } from '@/hooks/use-editor';
import { useLocaleStore } from '@/i18n';
import { checkIsInArtTextGeneratorMode } from '@/utils/mode';
import { TextPanelContext } from '../../context';
import { useEditingRange, useLayerProps, useTextMark } from '../../hooks';
import { getRichTextEditableProp, Tracker } from '../../utils';

type ListenedProps = Pick<
    ITextLayerProps,
    | 'bold'
    | 'italic'
    | 'lineThrough'
    | 'underline'
    | 'writingMode'
    | 'texture'
    | 'textGradient'
    | 'textContents'
    | 'advancedTextMaterial'
>;

export const FontStyle: React.FC = () => {
    const { $t } = useLocaleStore();

    const { layerId, recordId } = useContext(TextPanelContext)!;

    const editor = useEditor();
    const props = useLayerProps(editor, (layer) => {
        if (!layer) {
            return {
                bold: false,
                italic: false,
                lineThrough: false,
                underline: false,
                writingMode: TextWritingType.HORIZONTAL_TB,
                texture: null,
                textGradient: null,
                textContents: [],
                advancedTextMaterial: null,
            };
        }

        const layerData = layer.data as ITextLayerProps;
        const {
            bold,
            italic,
            lineThrough,
            underline,
            writingMode,
            texture,
            textGradient,
            textContents,
            advancedTextMaterial,
        } = layerData;

        return {
            bold,
            italic,
            lineThrough,
            underline,
            writingMode,
            texture,
            textGradient,
            textContents,
            advancedTextMaterial,
        };
    }) as ListenedProps;

    const { hasNoTextMark } = useTextMark(editor);
    const editingRange = useEditingRange(editor);

    const bold = getRichTextEditableProp<boolean>(props.textContents, editingRange, 'bold', props.bold);
    const italic = getRichTextEditableProp<boolean>(props.textContents, editingRange, 'italic', props.italic);
    const lineThrough = getRichTextEditableProp<boolean>(
        props.textContents,
        editingRange,
        'lineThrough',
        props.lineThrough,
    );
    const underline = getRichTextEditableProp<boolean>(props.textContents, editingRange, 'underline', props.underline);

    const writingMode = props.writingMode;
    const texture = props.texture;
    const textGradient = props.textGradient;
    const advancedTextMaterial = props.advancedTextMaterial;

    const isUsingTexture = !!texture && !!texture.url;
    const isUsingGradient = !!textGradient && textGradient.colors.length > 1;
    const isInArtTextGeneratorMode = checkIsInArtTextGeneratorMode();
    const ignoreRange = !isInArtTextGeneratorMode && !!advancedTextMaterial;

    const disableWritingMode = (() => {
        if (!hasNoTextMark) return $t((v) => v.textMarkSelector.disabledByTextMark);
        if (advancedTextMaterial) return true;
        return false;
    })();
    const disableUnderline = isUsingTexture || isUsingGradient;
    const disableLineThrough = isUsingTexture || isUsingGradient;

    const handleChange = (kind: Features, value: boolean | string) => {
        switch (kind) {
            case 'bold':
                if (isInArtTextGeneratorMode) {
                    return editor
                        .getInstance(TextLayerExtension)
                        .adjustArtTextSingleCharacter(layerId, { bold: value as boolean }, { writeHistory: true });
                }
                editor.getInstance(TextLayerExtension).setBoldStyle(layerId, value as boolean, { ignoreRange });
                break;
            case 'italic':
                if (isInArtTextGeneratorMode) {
                    return editor
                        .getInstance(TextLayerExtension)
                        .adjustArtTextSingleCharacter(layerId, { italic: value as boolean }, { writeHistory: true });
                }
                editor.getInstance(TextLayerExtension).setItalicStyle(layerId, value as boolean, { ignoreRange });
                break;
            case 'lineThrough':
                if (isInArtTextGeneratorMode) {
                    return editor
                        .getInstance(TextLayerExtension)
                        .adjustArtTextSingleCharacter(
                            layerId,
                            { lineThrough: value as boolean },
                            { writeHistory: true },
                        );
                }
                editor.getInstance(TextLayerExtension).setLineThrough(layerId, value as boolean, { ignoreRange });
                break;
            case 'underline':
                if (isInArtTextGeneratorMode) {
                    return editor
                        .getInstance(TextLayerExtension)
                        .adjustArtTextSingleCharacter(layerId, { underline: value as boolean }, { writeHistory: true });
                }
                editor.getInstance(TextLayerExtension).setUnderline(layerId, value as boolean, { ignoreRange });
                break;
            case 'writingMode':
                editor.getInstance(TextLayerExtension).setWritingMode(layerId, value as TextWritingType);
                break;
        }

        Tracker.setFontStyle({
            recordId,
            stageType: editor.stage.getType(),
            type: kind,
            value,
        });
    };

    return (
        <FontStyleGroup
            disableWritingMode={disableWritingMode}
            disableLineThrough={disableLineThrough}
            disableUnderline={disableUnderline}
            bold={bold}
            italic={italic}
            lineThrough={lineThrough}
            underline={underline}
            writingMode={writingMode}
            onChange={handleChange}
        />
    );
};
